import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Pencil, RotateCcw, Undo2, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { ReopenDialog } from "./ReopenDialog";
import { ReasonDialog } from "./ReasonDialog";
import {
    SC_TIMEOUT_MS, STATUS_TONE, fmt, fmtCost, fmtDateTime, fmtDay, fmtMoney, fmtPeriod, fmtQuantity, labelOf, scMessage,
} from "./scUtils";

const P = OPS.sc.panel;
const title = "text-xs font-bold text-[color:var(--ink-muted)]";
const chip = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold";
const HISTORY_KEYS = ["submit", "reopen", "approve", "unapprove", "zero_fill"];

/** `by` is `{id, name}` on the server; tolerate a bare string. */
const who = (b) => (b && typeof b === "object" ? b.name : b) || "";

function Facts({ sheet, scope }) {
    const { t, lang } = useI18n();
    const c = sheet.count || {};
    const loc = sheet.location || {};
    const rows = [
        ["kind", labelOf(scope?.labels?.kinds, loc.kind, lang)],
        ["erp_store", loc.erp_store, true],
        ["window", sheet.window?.start ? `${fmtDay(sheet.window.start, lang)} – ${fmtDay(sheet.window.end, lang)}` : ""],
        ["open_until", c.open_until ? fmtDay(c.open_until, lang) : ""],
        ["updated", c.updated_at ? `${who(c.updated_by)} · ${fmtDateTime(c.updated_at, lang)}` : ""],
        ["submitted", c.submitted_at ? `${who(c.submitted_by)} · ${fmtDateTime(c.submitted_at, lang)}` : ""],
        ["approved", c.approved_at ? `${who(c.approved_by)} · ${fmtDateTime(c.approved_at, lang)}` : ""],
    ].filter(([, v]) => v !== null && v !== undefined && v !== "");
    return (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5">
            {rows.map(([k, v, ltr]) => (
                <div key={k} className="min-w-0">
                    <dt className="text-[11px] text-[color:var(--ink-faint)]">{t(`stk_panel_field_${k}`)}</dt>
                    <dd className="break-words text-sm font-semibold text-[color:var(--ink)]">{ltr ? <bdi dir="ltr">{v}</bdi> : v}</dd>
                </div>
            ))}
        </dl>
    );
}

function Lines({ sheet, scope }) {
    const { t, tn, lang } = useI18n();
    const values = sheet.values;
    const lines = sheet.lines || {};
    // the server's item order (group, sort_order, name); only counted lines here
    const counted = (sheet.items || []).filter((i) => lines[i.id]);
    if (!counted.length) {
        return <p className="rounded-[var(--radius-md)] border border-dashed border-[color:var(--border-soft)] px-3 py-6 text-center text-xs text-[color:var(--ink-muted)]">{t("stk_panel_no_lines")}</p>;
    }
    return (
        <ul className="divide-y divide-[color:var(--border-soft)] rounded-[var(--radius-md)] border border-[color:var(--border-soft)] px-3">
            {counted.map((i) => {
                const ln = lines[i.id];
                const v = values ? values[i.id] : null;
                const unit = ln.unit || i.count_unit;
                const packed = ln.packs !== null && ln.packs !== undefined && i.pack_unit;
                return (
                    <li key={i.id} data-testid={P.line(i.id)} className="space-y-0.5 py-2">
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="min-w-0 truncate text-sm font-semibold text-[color:var(--ink)]">{tn(i)}</span>
                            {values ? (
                                <span className="shrink-0 text-sm font-bold text-[color:var(--ink)]">
                                    {v && v.value !== null && v.value !== undefined
                                        ? <bdi dir="ltr">{fmtMoney(v.value, lang)}</bdi>
                                        : <span className={`${chip} bg-[color:var(--warning-soft)] text-[color:var(--warning-soft-fg)]`}>{t("stk_panel_no_cost")}</span>}
                                </span>
                            ) : null}
                        </div>
                        <div className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-[color:var(--ink-muted)]">
                            <bdi dir="ltr" className="text-[color:var(--ink-faint)]">{i.code}</bdi>
                            <span><bdi dir="ltr" className="font-bold text-[color:var(--ink)]">{fmtQuantity(ln.quantity, lang)}</bdi> {unit}</span>
                            {packed ? (
                                <span>(<bdi dir="ltr">{fmtQuantity(ln.packs, lang)}</bdi> {i.pack_unit} + <bdi dir="ltr">{fmtQuantity(ln.loose || 0, lang)}</bdi> {i.count_unit})</span>
                            ) : null}
                            {v && v.unit_cost !== null && v.unit_cost !== undefined ? (
                                <span>× <bdi dir="ltr">{fmtCost(v.unit_cost, lang)}</bdi> {lang === "ar" ? "ر.س" : "SAR"}</span>
                            ) : null}
                            {v && v.basis ? (
                                <span className="text-[color:var(--ink-faint)]">
                                    {labelOf(scope?.labels?.bases, v.basis, lang)}{v.cost_period ? <> <bdi dir="ltr">{v.cost_period}</bdi></> : null}
                                </span>
                            ) : null}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

function History({ history }) {
    const { t, lang } = useI18n();
    const rows = [...(history || [])].reverse();
    if (!rows.length) return null;
    return (
        <section data-testid={P.history} className="space-y-1.5">
            <h4 className={title}>{t("stk_panel_history")}</h4>
            <ol className="space-y-2">
                {rows.map((h, idx) => (
                    <li key={`${h.at}-${idx}`} data-action={h.action}
                        className="rounded-[var(--radius-md)] bg-[color:var(--bg-surface-2)] px-3 py-2 text-xs">
                        <p className="font-bold text-[color:var(--ink)]">
                            {HISTORY_KEYS.includes(h.action) ? t(`stk_panel_action_${h.action}`) : h.action}
                            {h.until ? <span className="font-semibold text-[color:var(--ink-muted)]"> · {t("stk_panel_field_open_until")} {fmtDay(h.until, lang)}</span> : null}
                        </p>
                        <p className="text-[11px] text-[color:var(--ink-faint)]">{who(h.by)} · {fmtDateTime(h.at, lang)}</p>
                        {h.reason ? <p className="mt-0.5 break-words text-[11px] text-[color:var(--ink-muted)]">{h.reason}</p> : null}
                    </li>
                ))}
            </ol>
        </section>
    );
}

/** The board's side panel for one location + period: lines with values, totals,
 *  history, and finance's actions — approve, unapprove (reason), reopen (reason
 *  + until date) — plus «تعديل», which opens the count tab on this sheet. */
export function SheetPanel({ locationId, period, scope, onClose, onChanged }) {
    const { t, tn, lang } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const [sheet, setSheet] = useState(null);
    const [failed, setFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [busy, setBusy] = useState(false);
    const [dialog, setDialog] = useState(null);   // "reopen" | "unapprove" | null

    // a different sheet starts blank; a reload after an action keeps the old one on screen
    useEffect(() => { setSheet(null); setFailed(false); setDialog(null); }, [locationId, period]);
    useEffect(() => {
        if (!locationId || !period) return undefined;
        let alive = true;
        setFailed(false);
        api.get("/sc/sheet", { params: { location_id: locationId, period } })
            .then((r) => { if (alive) setSheet(r.data); })
            .catch((e) => {
                if (!alive) return;
                const msg = scMessage(e, lang);
                if (msg) { toast.error(msg); onClose(); } else setFailed(true);
            });
        return () => { alive = false; };
    }, [locationId, period, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const caps = scope?.caps || {};
    const status = sheet?.count?.status || "not_started";
    const locName = sheet ? tn(sheet.location) : "";
    const totals = sheet?.totals || {};
    const n = useMemo(() => new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US"), [lang]);

    /** One finance action; `onConfirm` callers rely on it never throwing. */
    const act = async (path, body, okKey) => {
        setBusy(true);
        try {
            await api.post(`/sc/sheet/${path}`, { location_id: locationId, period, ...body }, { timeout: SC_TIMEOUT_MS });
            toast.success(t(okKey));
            setDialog(null);
            setReloadKey((k) => k + 1);
            onChanged && onChanged();
        } catch (e) {
            toast.error(scMessage(e, lang) || t("stk_admin_offline_error"));
            // 409: the count moved on meanwhile (approved / submitted elsewhere) — show its real state
            if (e?.response?.status === 409) {
                setReloadKey((k) => k + 1);
                onChanged && onChanged();
            }
        } finally {
            setBusy(false);
        }
    };

    const goEdit = () => {
        const next = new URLSearchParams(searchParams);
        next.set("section", "sc");
        next.set("tab", "count");
        next.set("loc", locationId);
        next.set("period", period);
        onClose();
        setSearchParams(next);
    };

    const btn = "flex h-11 w-full items-center justify-center gap-2 rounded-full text-sm font-bold transition-[background-color,opacity] duration-150 disabled:opacity-50";

    return (
        <Sheet open={!!locationId} onOpenChange={(o) => { if (!o) onClose(); }}>
            <SheetContent side="right" data-testid={P.sheet} className="w-full overflow-y-auto bg-[color:var(--bg-surface)] p-0 sm:max-w-lg">
                <SheetHeader className="sticky top-0 z-10 border-b border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] px-4 py-3 pr-12 text-start sm:text-start">
                    <SheetTitle className="text-base font-extrabold text-[color:var(--ink)]">
                        {locName || t("stk_panel_title")}
                    </SheetTitle>
                    <SheetDescription className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--ink-muted)]">
                        <span>{fmtPeriod(period, lang)}</span>
                        {sheet ? (
                            <span data-status={status} className={`${chip} ${STATUS_TONE[status] || ""}`}>
                                {labelOf(scope?.labels?.statuses, status, lang)}
                            </span>
                        ) : null}
                    </SheetDescription>
                </SheetHeader>
                {failed ? (
                    <div className="p-4">
                        <div data-testid={OPS.sc.offlineError}
                             className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:rgba(220,38,38,0.08)] px-3 py-2">
                            <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                                <WifiOff className="h-3.5 w-3.5" />{t("stk_admin_offline_error")}
                            </span>
                            <Button size="sm" variant="outline" data-testid={OPS.sc.retry} onClick={() => setReloadKey((k) => k + 1)}>
                                {t("stk_admin_retry")}
                            </Button>
                        </div>
                    </div>
                ) : !sheet ? <InlineLoader /> : (
                    <div className="space-y-5 p-4">
                        <section data-testid={P.total}
                                 className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            <div className="min-w-0 rounded-[var(--radius-md)] border border-[color:var(--border-soft)] px-2.5 py-2">
                                <p className="text-[10px] font-bold text-[color:var(--ink-muted)]">{t("stk_panel_counted")}</p>
                                <p className="mt-1 truncate text-sm font-extrabold text-[color:var(--ink)]">
                                    <bdi dir="ltr">{n.format(totals.counted_n || 0)}/{n.format(totals.items_n || 0)}</bdi>
                                </p>
                            </div>
                            {sheet.values ? (
                                <>
                                    {/* the riyal value gets the full width (and the top) on a phone */}
                                    <div className="order-first col-span-2 min-w-0 rounded-[var(--radius-md)] border border-[color:var(--brand-orange)]/40 bg-[color:var(--brand-orange-soft)] px-2.5 py-2 sm:order-none sm:col-span-1">
                                        <p className="text-[10px] font-bold text-[color:var(--ink-muted)]">{t("stk_panel_value")}</p>
                                        <p data-value={totals.value ?? ""} className="mt-1 truncate text-sm font-extrabold text-[color:var(--ink)]">
                                            <bdi dir="ltr">{fmtMoney(totals.value || 0, lang)}</bdi>
                                        </p>
                                    </div>
                                    <div className="min-w-0 rounded-[var(--radius-md)] border border-[color:var(--border-soft)] px-2.5 py-2">
                                        <p className="text-[10px] font-bold text-[color:var(--ink-muted)]">{t("stk_panel_missing_cost")}</p>
                                        <p className={`mt-1 truncate text-sm font-extrabold ${totals.missing_cost_n ? "text-[color:var(--warning-soft-fg)]" : "text-[color:var(--ink)]"}`}>
                                            <bdi dir="ltr">{n.format(totals.missing_cost_n || 0)}</bdi>
                                        </p>
                                    </div>
                                </>
                            ) : null}
                        </section>
                        {status === "approved" && sheet.values ? (
                            <p className="text-[11px] text-[color:var(--ink-faint)]">{t("stk_panel_snapshot_note")}</p>
                        ) : null}

                        <Facts sheet={sheet} scope={scope} />

                        <section className="space-y-1.5">
                            <h4 className={title}>
                                {t("stk_panel_lines")}
                                {totals.items_n > totals.counted_n ? (
                                    <span className="font-semibold text-[color:var(--ink-faint)]"> · {fmt(t("stk_panel_uncounted"), { n: n.format((totals.items_n || 0) - (totals.counted_n || 0)) })}</span>
                                ) : null}
                            </h4>
                            <Lines sheet={sheet} scope={scope} />
                        </section>

                        <History history={sheet.count?.history} />

                        <div className="space-y-2">
                            {sheet.editable ? (
                                <button type="button" data-testid={P.edit} onClick={goEdit}
                                        className={`${btn} border border-[color:var(--brand-brown)] text-[color:var(--brand-brown)] hover:bg-[color:var(--brand-orange-soft)]`}>
                                    <Pencil className="h-4 w-4" />{t("stk_panel_edit")}
                                </button>
                            ) : null}
                            {caps.can_finance && status === "submitted" ? (
                                <button type="button" data-testid={P.approve} disabled={busy} onClick={() => act("approve", {}, "stk_panel_approved")}
                                        className={`${btn} bg-[color:var(--brand-orange)] text-white shadow-[var(--shadow-sm)] hover:opacity-90`}>
                                    <CheckCircle2 className="h-4 w-4" />{busy && !dialog ? t("stk_admin_saving") : t("stk_panel_approve")}
                                </button>
                            ) : null}
                            {caps.can_finance && status === "approved" ? (
                                <button type="button" data-testid={P.unapprove} disabled={busy} onClick={() => setDialog("unapprove")}
                                        className={`${btn} border border-[color:var(--danger-soft-fg)] text-[color:var(--danger-soft-fg)] hover:bg-[color:var(--danger-soft)]`}>
                                    <Undo2 className="h-4 w-4" />{t("stk_panel_unapprove")}
                                </button>
                            ) : null}
                            {caps.can_finance && status !== "approved" ? (
                                <button type="button" data-testid={P.reopen} disabled={busy} onClick={() => setDialog("reopen")}
                                        className={`${btn} border border-[color:var(--border-soft)] text-[color:var(--ink)] hover:bg-[color:var(--bg-surface-2)]`}>
                                    <RotateCcw className="h-4 w-4" />{t("stk_panel_reopen")}
                                </button>
                            ) : null}
                            <button type="button" data-testid={P.close} onClick={onClose}
                                    className={`${btn} border border-[color:var(--border-soft)] text-[color:var(--ink)] hover:bg-[color:var(--bg-surface-2)]`}>
                                {t("stk_panel_close")}
                            </button>
                        </div>
                    </div>
                )}
                <ReopenDialog open={dialog === "reopen"} onOpenChange={(o) => setDialog(o ? "reopen" : null)} scope={scope}
                              locationName={locName} onConfirm={(body) => act("reopen", body, "stk_panel_reopened")} />
                <ReasonDialog open={dialog === "unapprove"} onOpenChange={(o) => setDialog(o ? "unapprove" : null)}
                              title={t("stk_panel_unapprove_title")} description={t("stk_panel_unapprove_body")}
                              confirmLabel={t("stk_panel_unapprove")} destructive
                              onConfirm={(reason) => act("unapprove", { reason }, "stk_panel_unapproved")} />
            </SheetContent>
        </Sheet>
    );
}
